import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { defaultPromoPopup } from '../../data/products';
import { 
  Gift, 
  Save, 
  RotateCcw, 
  Eye, 
  Sparkles, 
  Upload, 
  Tag, 
  CheckCircle2,
  X,
  Image as ImageIcon,
  ExternalLink
} from 'lucide-react';

export default function AdminPopupManager() {
  const { 
    promoPopupData, 
    updatePromoPopup, 
    setIsAdminMode,
    setIsPromoPopupDismissed,
    addToast 
  } = useShop();

  const [form, setForm] = useState({ 
    imageFit: 'cover',
    ...promoPopupData 
  });
  const [isProcessingImg, setIsProcessingImg] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    updatePromoPopup(form);
  };

  const handleReset = () => {
    setForm({ ...defaultPromoPopup, imageFit: 'cover' });
    updatePromoPopup({ ...defaultPromoPopup, imageFit: 'cover' });
    addToast('Promo popup reset to festive defaults', 'info');
  };

  // Optimized image compression & resizing
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessingImg(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 960;
        const scale = Math.min(1, MAX_WIDTH / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const optimizedDataUrl = canvas.toDataURL('image/jpeg', 0.86);
        setForm((prev) => ({ ...prev, bannerImg: optimizedDataUrl }));
        setIsProcessingImg(false);
        addToast('Photo optimized and loaded for popup!', 'success');
      };
      img.onerror = () => {
        setIsProcessingImg(false);
        addToast('Could not process image file', 'error');
      };
      img.src = event.target?.result;
    };
    reader.readAsDataURL(file);
  };

  const handleTestOnStorefront = () => {
    updatePromoPopup({ ...form, isEnabled: true });
    setIsPromoPopupDismissed(false);
    try { sessionStorage.removeItem('ml_promo_popup_dismissed'); } catch (_) {}
    setIsAdminMode(false);
    addToast('Opening storefront with active promotional popup!', 'success');
  };

  return (
    <div style={{ maxWidth: '980px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--gold-400)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.3rem' }}>
            <Sparkles size={13} /> High-Conversion Marketing CMS
          </div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>
            Festive Discount Popup & <span className="text-gold-gradient">Promotion Modal</span>
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Active offer modal shown to customers on storefront to promote discounts and coupon codes.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={handleTestOnStorefront}
            className="btn btn-outline btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}
          >
            <ExternalLink size={14} />
            <span>Test on Storefront</span>
          </button>

          <button
            type="button"
            onClick={handleReset}
            className="btn btn-glass btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem' }}
          >
            <RotateCcw size={14} />
            <span>Reset Defaults</span>
          </button>
        </div>
      </div>

      {/* Live Preview */}
      <div style={{
        background: '#0d0d16',
        border: '1px solid var(--border-gold)',
        borderRadius: 'var(--radius-md)',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--gold-400)', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>
            <Eye size={15} /> Storefront Modal Live Preview
          </div>

          <span style={{
            fontSize: '0.72rem',
            padding: '0.2rem 0.6rem',
            borderRadius: '999px',
            fontWeight: 700,
            backgroundColor: form.isEnabled ? 'rgba(52, 211, 153, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            color: form.isEnabled ? '#34d399' : '#f87171',
            border: form.isEnabled ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)'
          }}>
            Status: {form.isEnabled ? 'ACTIVE (LIVE)' : 'DISABLED'}
          </span>
        </div>

        <div style={{
          maxWidth: '460px',
          margin: '0 auto',
          background: '#13131e',
          border: '1px solid var(--border-gold)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8), 0 0 30px rgba(212, 175, 55, 0.15)'
        }}>
          {form.bannerImg ? (
            <div style={{ position: 'relative', height: '190px', backgroundColor: '#0a0a0f', overflow: 'hidden' }}>
              <img
                src={form.bannerImg}
                alt="Promo Preview"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: form.imageFit === 'contain' ? 'contain' : 'cover' 
                }}
              />
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to bottom, transparent 40%, #13131e 100%)'
              }} />
              {form.discountBadge && (
                <span style={{
                  position: 'absolute',
                  top: '12px',
                  left: '12px',
                  background: 'var(--gold-gradient)',
                  color: '#0a0a0f',
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  padding: '0.2rem 0.6rem',
                  borderRadius: '999px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.4)'
                }}>
                  {form.discountBadge}
                </span>
              )}
            </div>
          ) : (
            <div style={{ padding: '2rem 1rem 1rem', textAlign: 'center', background: '#0e0e17' }}>
              <Gift size={36} color="var(--gold-400)" />
            </div>
          )}

          <div style={{ padding: '1.5rem', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', marginBottom: '0.4rem' }}>
              {form.title}
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', marginBottom: '1.2rem', lineHeight: 1.5 }}>
              {form.subtitle}
            </p>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(212, 175, 55, 0.12)',
              border: '1px dashed var(--border-gold)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.45rem 1rem',
              color: 'var(--gold-300)',
              fontWeight: 800,
              fontSize: '1rem',
              fontFamily: 'monospace',
              marginBottom: '1.2rem'
            }}>
              <Tag size={15} />
              <span>{form.couponCode}</span>
            </div>

            <div>
              <button type="button" className="btn btn-gold btn-sm" style={{ width: '100%', padding: '0.65rem' }}>
                <span>{form.btnText}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <form onSubmit={handleSubmit} style={{
        background: '#13131e',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.4rem'
      }}>
        {/* Toggle Switch */}
        <div style={{
          padding: '1rem 1.25rem',
          background: form.isEnabled ? 'rgba(52, 211, 153, 0.08)' : 'rgba(255, 255, 255, 0.02)',
          border: form.isEnabled ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-sm)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <div style={{ fontWeight: 800, color: '#fff', fontSize: '0.95rem' }}>
              Activate Festive Promotional Popup
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              When enabled, visitors will see this offer modal with the image, discount, and coupon code.
            </div>
          </div>

          <label style={{ position: 'relative', display: 'inline-block', width: '52px', height: '28px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.isEnabled}
              onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span style={{
              position: 'absolute',
              cursor: 'pointer',
              inset: 0,
              backgroundColor: form.isEnabled ? '#34d399' : '#333',
              borderRadius: '28px',
              transition: '0.3s'
            }}>
              <span style={{
                position: 'absolute',
                height: '20px',
                width: '20px',
                left: form.isEnabled ? '28px' : '4px',
                bottom: '4px',
                backgroundColor: '#fff',
                borderRadius: '50%',
                transition: '0.3s'
              }} />
            </span>
          </label>
        </div>

        {/* Image Upload Box */}
        <div style={{
          padding: '1.25rem',
          backgroundColor: '#0d0d16',
          border: '1px dashed var(--border-gold)',
          borderRadius: 'var(--radius-md)'
        }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 800, color: 'var(--gold-400)', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
            Popup Photo / Image Upload (Direct File Picker)
          </label>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
            Upload any promotional banner, jewelry photo, or festival poster. It will be automatically fitted.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
            <label className="btn btn-gold btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <Upload size={15} />
              <span>{isProcessingImg ? 'Optimizing...' : 'Upload Image from Computer/Phone'}</span>
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleFileUpload} 
                style={{ display: 'none' }} 
              />
            </label>

            {form.bannerImg && (
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, bannerImg: '' }))}
                className="btn btn-glass btn-sm"
                style={{ color: '#f87171' }}
              >
                <X size={14} /> Remove Photo
              </button>
            )}

            {/* Fit mode selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Image Fit:</span>
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, imageFit: 'cover' }))}
                style={{
                  background: form.imageFit === 'cover' ? 'var(--gold-gradient)' : 'rgba(255, 255, 255, 0.05)',
                  color: form.imageFit === 'cover' ? '#0a0a0f' : '#fff',
                  border: 'none',
                  padding: '0.3rem 0.65rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Cover (Full)
              </button>
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, imageFit: 'contain' }))}
                style={{
                  background: form.imageFit === 'contain' ? 'var(--gold-gradient)' : 'rgba(255, 255, 255, 0.05)',
                  color: form.imageFit === 'contain' ? '#0a0a0f' : '#fff',
                  border: 'none',
                  padding: '0.3rem 0.65rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Contain (Fit Entire Photo)
              </button>
            </div>
          </div>

          {/* Fallback URL input */}
          <div style={{ marginTop: '0.85rem' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Or enter Image URL directly:</span>
            <input
              type="text"
              value={form.bannerImg || ''}
              onChange={(e) => setForm({ ...form, bannerImg: e.target.value })}
              className="input-luxury"
              placeholder="https://..."
              style={{ marginTop: '0.25rem', fontSize: '0.8rem' }}
            />
          </div>
        </div>

        {/* Modal Text Fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem', textTransform: 'uppercase' }}>
              Modal Title
            </label>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="input-luxury"
              placeholder="e.g. Navratri & Dhanteras Privilege"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem', textTransform: 'uppercase' }}>
              Discount Tag Badge
            </label>
            <input
              type="text"
              required
              value={form.discountBadge}
              onChange={(e) => setForm({ ...form, discountBadge: e.target.value })}
              className="input-luxury"
              placeholder="e.g. FLAT 10% OFF"
            />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem', textTransform: 'uppercase' }}>
            Promotional Message Subtitle
          </label>
          <textarea
            rows={2}
            required
            value={form.subtitle}
            onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
            className="input-luxury"
            style={{ resize: 'vertical' }}
            placeholder="e.g. Avail flat 10% discount on hallmarked pure gold & certified solitaire jewellery collections."
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem', textTransform: 'uppercase' }}>
              Coupon Code Attached
            </label>
            <input
              type="text"
              required
              value={form.couponCode}
              onChange={(e) => setForm({ ...form, couponCode: e.target.value.toUpperCase() })}
              className="input-luxury"
              style={{ textTransform: 'uppercase', fontWeight: 800 }}
              placeholder="e.g. ROYAL10"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem', textTransform: 'uppercase' }}>
              Call-to-Action Button Text
            </label>
            <input
              type="text"
              required
              value={form.btnText}
              onChange={(e) => setForm({ ...form, btnText: e.target.value })}
              className="input-luxury"
              placeholder="e.g. Claim Offer & Shop Now"
            />
          </div>
        </div>

        <div style={{ paddingTop: '0.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            type="submit"
            className="btn btn-gold"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.8rem 2.2rem' }}
          >
            <Save size={16} />
            <span>Save & Publish Promo Popup</span>
          </button>

          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Changes will take effect instantly across the storefront.
          </span>
        </div>
      </form>
    </div>
  );
}
